import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Plus, Mail, Users, CheckCircle2, AlertTriangle, Download, CheckCircle } from 'lucide-react';

const STATUS_TONE = {
    draft: 'bg-slate-50 text-slate-600 border-slate-200',
    queued: 'bg-sky-50 text-sky-700 border-sky-200',
    running: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    paused: 'bg-amber-50 text-amber-700 border-amber-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    cancelled: 'bg-red-50 text-red-700 border-red-200',
};

const TABS = [
    { key: 'campaigns', label: 'Campaigns', icon: Mail },
    { key: 'failed', label: 'Failed deliveries', icon: AlertTriangle },
];

export default function EmailCampaigns() {
    const navigate = useNavigate();
    const [tab, setTab] = useState('campaigns');
    const [items, setItems] = useState([]);
    const [failed, setFailed] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try { const r = await api.get('/leadgen/email-campaigns'); setItems(r.data.items); }
        catch { /* noop */ } finally { setLoading(false); }
    }, []);
    const loadFailed = useCallback(async () => {
        setLoading(true);
        try { const r = await api.get('/leadgen/email-campaigns/failed-deliveries'); setFailed(r.data.items); }
        catch { /* noop */ } finally { setLoading(false); }
    }, []);
    useEffect(() => { if (tab === 'campaigns') load(); else loadFailed(); }, [tab, load, loadFailed]);

    const exportCsv = () => {
        const base = process.env.REACT_APP_BACKEND_URL;
        window.open(`${base}/api/leadgen/email-campaigns/failed-deliveries/export`, '_blank');
    };

    return (
        <div data-testid="email-campaigns-page" className="bm-fade-in">
            <PageHeader
                title="Email Campaigns"
                description="Individual, personalised Microsoft 365 outreach — one email per contact, never BCC."
                actions={
                    <Button onClick={() => navigate('/leadgen/email-campaigns/new')} style={{ background: 'var(--bm-primary)' }} data-testid="new-campaign-btn">
                        <Plus size={14} className="mr-1.5" /> New Campaign
                    </Button>
                }
            />
            <div className="px-8 pt-4 flex items-center gap-1.5" data-testid="email-campaigns-tabs">
                {TABS.map(t => {
                    const Icon = t.icon;
                    return (
                        <button key={t.key} onClick={() => setTab(t.key)} data-testid={`email-campaigns-tab-${t.key}`}
                                className={`flex items-center gap-1.5 text-[12.5px] font-medium px-3 py-2 rounded-lg transition ${tab === t.key ? 'bg-[color:var(--bm-accent-50)] text-[color:var(--bm-primary)]' : 'text-[color:var(--bm-text-muted)] hover:bg-[color:var(--bm-bg-mute)]'}`}>
                            <Icon size={14} /> {t.label}
                            {t.key === 'failed' && failed.length > 0 && (
                                <span className="ml-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">{failed.length}</span>
                            )}
                        </button>
                    );
                })}
            </div>
            <div className="px-8 py-6">
                {loading && <div className="text-center text-[color:var(--bm-text-muted)] py-10">Loading…</div>}

                {!loading && tab === 'campaigns' && items.length === 0 && (
                    <div className="bg-white rounded-xl border border-dashed border-[color:var(--bm-border)] px-5 py-16 text-center">
                        <Mail size={32} className="mx-auto text-[color:var(--bm-text-subtle)]" />
                        <div className="mt-3 font-display text-[16px] font-semibold">No campaigns yet</div>
                        <div className="text-[13px] text-[color:var(--bm-text-muted)] mt-1">Import some prospects, then create your first outreach campaign.</div>
                        <Button onClick={() => navigate('/leadgen/email-campaigns/new')} className="mt-4" style={{ background: 'var(--bm-primary)' }}>
                            <Plus size={14} className="mr-1.5" /> New Campaign
                        </Button>
                    </div>
                )}
                {!loading && tab === 'campaigns' && items.length > 0 && (
                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden">
                        <table className="w-full text-[13px]">
                            <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                                <tr>
                                    <th className="text-left font-medium px-5 py-3">Campaign</th>
                                    <th className="text-left font-medium px-4 py-3">Status</th>
                                    <th className="text-right font-medium px-4 py-3">Contacts</th>
                                    <th className="text-right font-medium px-4 py-3">Sent</th>
                                    <th className="text-right font-medium px-4 py-3">Replied</th>
                                    <th className="text-left font-medium px-4 py-3">Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(c => (
                                    <tr key={c.id} onClick={() => navigate(`/leadgen/email-campaigns/${c.id}`)}
                                        className="border-t border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)] cursor-pointer" data-testid={`campaign-row-${c.id}`}>
                                        <td className="px-5 py-3 font-medium">{c.name}</td>
                                        <td className="px-4 py-3"><span className={`text-[10.5px] font-semibold uppercase tracking-wide px-2 py-1 rounded border ${STATUS_TONE[c.status] || ''}`}>{c.status}</span></td>
                                        <td className="px-4 py-3 text-right">{c.total_contacts}</td>
                                        <td className="px-4 py-3 text-right"><span className="inline-flex items-center gap-1"><Users size={12} className="text-[color:var(--bm-text-subtle)]" />{c.sent_count}</span></td>
                                        <td className="px-4 py-3 text-right"><span className="inline-flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-500" />{c.replied_count}</span></td>
                                        <td className="px-4 py-3 text-[color:var(--bm-text-muted)]">{new Date(c.created_at).toLocaleDateString('en-GB')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {!loading && tab === 'failed' && (
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-[12.5px] text-[color:var(--bm-text-muted)]">Every failed send or bounce across all campaigns — fix the emails, then re-upload via Import Prospects → "Update existing contacts".</p>
                            <Button variant="outline" size="sm" onClick={exportCsv} disabled={failed.length === 0} className="h-8 shrink-0" data-testid="failed-deliveries-export-btn">
                                <Download size={13} className="mr-1.5" /> Export CSV
                            </Button>
                        </div>
                        {failed.length === 0 ? (
                            <div className="bg-white rounded-xl border border-dashed border-[color:var(--bm-border)] px-5 py-16 text-center">
                                <CheckCircle size={32} className="mx-auto text-emerald-500" />
                                <div className="mt-3 font-display text-[16px] font-semibold">No failed deliveries</div>
                                <div className="text-[13px] text-[color:var(--bm-text-muted)] mt-1">Every send across every campaign has gone through cleanly.</div>
                            </div>
                        ) : (
                            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden">
                                <table className="w-full text-[13px]">
                                    <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                                        <tr>
                                            <th className="text-left font-medium px-5 py-3">Contact</th>
                                            <th className="text-left font-medium px-4 py-3">Company</th>
                                            <th className="text-left font-medium px-4 py-3">Email</th>
                                            <th className="text-left font-medium px-4 py-3">Campaign</th>
                                            <th className="text-left font-medium px-4 py-3">Type</th>
                                            <th className="text-left font-medium px-4 py-3">Reason</th>
                                            <th className="text-left font-medium px-4 py-3">Failed at</th>
                                            <th className="text-left font-medium px-4 py-3">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {failed.map(f => (
                                            <tr key={f.recipient_id} onClick={() => navigate(`/clients/${f.company_id}`)}
                                                className="border-t border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)] cursor-pointer" data-testid={`failed-delivery-row-${f.recipient_id}`}>
                                                <td className="px-5 py-3 font-medium">{f.contact_name || '—'}</td>
                                                <td className="px-4 py-3 text-[color:var(--bm-text-muted)]">{f.company_name || '—'}</td>
                                                <td className="px-4 py-3 text-[color:var(--bm-text-muted)]">{f.email || '—'}</td>
                                                <td className="px-4 py-3 text-[color:var(--bm-text-muted)]">{f.campaign_name}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`text-[10.5px] font-semibold uppercase tracking-wide px-2 py-1 rounded border ${f.failure_type === 'bounced' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{f.failure_type}</span>
                                                </td>
                                                <td className="px-4 py-3 text-[color:var(--bm-text-muted)] max-w-[220px] truncate" title={f.error_reason}>{f.error_reason || '—'}</td>
                                                <td className="px-4 py-3 text-[color:var(--bm-text-muted)]">{f.failed_at ? new Date(f.failed_at).toLocaleString('en-GB') : '—'}</td>
                                                <td className="px-4 py-3">
                                                    {f.already_corrected ? (
                                                        <span className="inline-flex items-center gap-1 text-[11.5px] font-medium text-emerald-700"><CheckCircle2 size={12} /> Corrected</span>
                                                    ) : (
                                                        <span className="text-[11.5px] text-[color:var(--bm-text-subtle)]">Needs fix</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
